import React from 'react';

const TermsOfService: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto p-6 bg-white text-gray-900">
      <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>

      <p className="mb-6">
        <strong>Important Notice:</strong><br />
        These Terms of Service ("Terms") govern your access to and use of Harmiton WebTech's services. By accessing or using our services, you agree to be bound by these Terms.
      </p>

      <h2 className="text-2xl font-semibold mb-4">1. Service Overview</h2>
      <p className="mb-6">
        Harmiton WebTech ("we," "us," or "our") provides web development, digital marketing, and related technology services ("Services") as described on our website <a href="https://harmiton.in" className="text-blue-600 underline">harmiton.in</a>.
      </p>

      <h2 className="text-2xl font-semibold mb-4">2. Account Registration</h2>
      <p className="mb-4">To access certain Services, you may need to register an account. You agree to:</p>
      <ul className="list-disc list-inside mb-6">
        <li>Provide accurate and complete registration information</li>
        <li>Maintain the security of your account credentials</li>
        <li>Accept responsibility for all activities under your account</li>
        <li>Notify us immediately of any unauthorized account use</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">3. Service Modifications</h2>
      <p className="mb-6">
        We reserve the right to modify or discontinue any Service (in whole or in part) at any time without notice. We shall not be liable for any modification, suspension, or discontinuation of any Service.
      </p>

      <h2 className="text-2xl font-semibold mb-4">4. Acceptable Use</h2>
      <p className="mb-4">You agree not to use our Services to:</p>
      <ul className="list-disc list-inside mb-6">
        <li>Violate any laws or regulations</li>
        <li>Infringe intellectual property rights</li>
        <li>Distribute malware or harmful code</li>
        <li>Engage in fraudulent activity</li>
        <li>Circumvent security measures</li>
        <li>Interfere with other users' enjoyment of Services</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">5. Client Responsibilities</h2>
      <p className="mb-4">When using our Services, you must:</p>
      <ul className="list-disc list-inside mb-6">
        <li>Provide complete and accurate project requirements</li>
        <li>Submit requested materials in agreed formats and timelines</li>
        <li>Appoint a single point of contact for project communication</li>
        <li>Provide timely feedback during review periods</li>
        <li>Make timely payments as agreed</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">6. Intellectual Property</h2>
      <h3 className="font-semibold mb-2">6.1 Our Materials</h3>
      <p className="mb-4">All pre-existing materials we provide remain our exclusive property. We grant you a limited license to use these materials solely for your project.</p>

      <h3 className="font-semibold mb-2">6.2 Client Materials</h3>
      <p className="mb-4">You retain ownership of materials you provide. You grant us a license to use these materials solely to provide the Services.</p>

      <h3 className="font-semibold mb-2">6.3 Deliverables</h3>
      <p className="mb-6">Upon full payment, we transfer rights to final deliverables as specified in your service agreement. Some deliverables may incorporate elements we retain rights to (e.g., frameworks, libraries).</p>

      <h2 className="text-2xl font-semibold mb-4">7. Payments</h2>
      <h3 className="font-semibold mb-2">7.1 Fees</h3>
      <p className="mb-4">Service fees are as specified in your proposal or service agreement. All fees are in INR unless otherwise stated.</p>

      <h3 className="font-semibold mb-2">7.2 Taxes</h3>
      <p className="mb-4">You are responsible for all applicable taxes. We will add GST (or other taxes if applicable) to invoices as required by law.</p>

      <h3 className="font-semibold mb-2">7.3 Late Payments</h3>
      <p className="mb-6">Late payments may incur interest at 1.5% per month. We may suspend Services for accounts more than 15 days overdue.</p>

      <h2 className="text-2xl font-semibold mb-4">8. Project Timelines</h2>
      <p className="mb-6">
        Estimated project timelines are based on timely receipt of all required materials and feedback from you. Delays in providing materials may extend project timelines.
      </p>

      <h2 className="text-2xl font-semibold mb-4">9. Revisions</h2>
      <p className="mb-6">
        Each project includes a specified number of revision rounds. Additional revisions may incur additional fees at our standard rates.
      </p>

      <h2 className="text-2xl font-semibold mb-4">10. Third-Party Services</h2>
      <p className="mb-6">
        Some Services may require third-party accounts (e.g., hosting, domain registration). You are responsible for maintaining these accounts and paying associated fees.
      </p>

      <h2 className="text-2xl font-semibold mb-4">11. Confidentiality</h2>
      <p className="mb-6">
        Both parties agree to keep confidential information secret using reasonable care, not to use it except as necessary, and to disclose it only to employees/contractors needing to know.
      </p>

      <h2 className="text-2xl font-semibold mb-4">12. Warranties</h2>
      <p className="mb-6">
        We warrant that Services will be performed with reasonable skill and care. Except as expressly stated, we disclaim all other warranties.
      </p>

      <h2 className="text-2xl font-semibold mb-4">13. Limitation of Liability</h2>
      <p className="mb-6">
        Our total liability for any claim related to Services is limited to fees you paid for the affected Service. We are not liable for indirect, incidental, or consequential damages.
      </p>

      <h2 className="text-2xl font-semibold mb-4">14. Termination</h2>
      <p className="mb-6">
        Either party may terminate Services with written notice. Termination may incur fees for work completed and non-cancelable expenses. Certain provisions survive termination.
      </p>

      <h2 className="text-2xl font-semibold mb-4">15. Disputes</h2>
      <p className="mb-6">
        Disputes will first be addressed through good faith negotiations. Unresolved disputes may be submitted to binding arbitration in Mumbai, India under Indian law.
      </p>

      <h2 className="text-2xl font-semibold mb-4">16. General</h2>
      <h3 className="font-semibold mb-2">16.1 Amendments</h3>
      <p className="mb-4">
        We may modify these Terms by posting updated versions on our website. Continued use after changes constitutes acceptance.
      </p>

      <h3 className="font-semibold mb-2">16.2 Entire Agreement</h3>
      <p className="mb-4">
        These Terms, with any service agreements, constitute the entire agreement between parties regarding the Services.
      </p>

      <h3 className="font-semibold mb-2">16.3 Governing Law</h3>
      <p className="mb-6">
        These Terms are governed by Indian law. Any disputes shall be subject to the exclusive jurisdiction of Mumbai courts.
      </p>

      <h2 className="text-2xl font-semibold mb-4">17. Contact Information</h2>
      <p>
        For questions about these Terms, contact us at:
      </p>
      <ul className="list-disc list-inside">
        <li>Harmiton WebTech</li>
        <li>Email: <a href="mailto:harmitonwt@gmail.com" className="text-blue-600 underline">harmitonwt@gmail.com</a></li>
        <li>Phone: +91 8451985096</li>
      </ul>
    </div>
  );
};

export default TermsOfService;
